import { collection, addDoc, getDocs, query, where, Timestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Product } from '../types';
import { usersService } from './usersService';

/** Recursively drop `undefined` values — Firestore rejects them. */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(v => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object' && !(value instanceof Date) && !(value instanceof Timestamp)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v !== undefined) out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

export const productsService = {
  async list(orgId?: string): Promise<Product[]> {
    const org = orgId || (await usersService.getCurrentUserOrgId());
    const q = query(collection(db, 'products'), where('orgId', '==', org || ''));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate(), updatedAt: d.data().updatedAt?.toDate() } as any));
  },
  async create(product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>, orgId?: string): Promise<string> {
    const org = orgId || (await usersService.getCurrentUserOrgId());
    const payload = stripUndefined({ ...product, orgId: org });
    const docRef = await addDoc(collection(db, 'products'), { ...payload, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
    return docRef.id;
  },
  async update(productId: string, updates: Partial<Product>): Promise<void> {
    const ref = doc(db, 'products', productId);
    await updateDoc(ref, { ...stripUndefined(updates), updatedAt: Timestamp.now() } as any);
  },
  async remove(productId: string): Promise<void> {
    await deleteDoc(doc(db, 'products', productId));
  },
  async removeDefaultServices(orgId?: string): Promise<number> {
    const org = orgId || (await usersService.getCurrentUserOrgId());
    const q = query(
      collection(db, 'products'),
      where('orgId', '==', org || ''),
      where('name', '==', 'Default Service')
    );
    const snap = await getDocs(q);
    let count = 0;
    for (const d of snap.docs) {
      await deleteDoc(doc(db, 'products', d.id));
      count++;
    }
    return count;
  },
};
